import {
  isLightweightArea,
  isLightweightGenericSheet,
} from "@/store/lightweight-elements-discriminators";
import { selectLightweightChildren } from "@/store/lightweight-elements-selectors";
import { RootState } from "@/store/store";
import { clearStore } from "@faro-lotv/app-component-toolbox";
import { GUID } from "@faro-lotv/ielement-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Maps area IDs to the selected sheet ID for the PDF report.
 * This is a dedicated state to track which sheet the user has selected per area
 * for the PDF report, independent of the global visible sheets state.
 */
export type PdfReportState = {
  /** Map of area ID to the selected sheet ID */
  selectedSheetByArea: Record<GUID, GUID>;
};

/** Helper type to only use the pdf report store in selectors. */
export type OnlyPdfReportStore = {
  pdfReport: PdfReportState;
};

const initialState: Readonly<PdfReportState> = Object.freeze({
  selectedSheetByArea: {},
});

const pdfReportSlice = createSlice({
  name: "pdfReport",
  initialState,
  reducers: {
    /**
     * Set the selected sheet for a specific area.
     *
     * @param state Current state
     * @param action The ID of the sheet to select for that area
     */
    setPdfReportSheetForArea(
      state,
      action: PayloadAction<{ areaId: GUID; sheetId: GUID }>,
    ) {
      state.selectedSheetByArea[action.payload.areaId] = action.payload.sheetId;
    },

    /**
     * Initialize the sheet selection map from the current visible sheets.
     * Called when entering PDF report mode.
     *
     * @param state Current state
     * @param action The initial sheet selection map
     */
    initPdfReportSheetSelection(
      state,
      action: PayloadAction<Record<GUID, GUID>>,
    ) {
      state.selectedSheetByArea = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const pdfReportReducer = pdfReportSlice.reducer;

export const { setPdfReportSheetForArea, initPdfReportSheetSelection } =
  pdfReportSlice.actions;

/**
 * Computes the initial area-to-sheet mapping based on current visible sheets.
 * For each area, finds the currently visible sheet; if none, uses the "best sheet" heuristic.
 *
 * @param state The current app state
 * @returns The initial mapping of area IDs to selected sheets based on the visible sheets state.
 */
export function computeInitialSheetSelection({
  selections,
  lightweightElements,
}: RootState): Record<GUID, GUID> {
  const areas = Object.values(lightweightElements.lightweightElements).filter(
    (el) => !!el && isLightweightArea(el),
  );

  const areaSheetMapping: Record<GUID, GUID> = {};

  for (const area of areas) {
    const sheetsInArea = selectLightweightChildren(
      area,
      isLightweightGenericSheet,
    )(lightweightElements);

    // Find a sheet in this area that's currently visible
    const visibleSheet = sheetsInArea.find(
      (s) => !!selections.visibleSheets?.[s.id],
    );

    if (visibleSheet) {
      areaSheetMapping[area.id] = visibleSheet.id;
    }
  }

  return areaSheetMapping;
}
